// Shared transaction room — accessible by seller (beta token) and buyer (UUID token in URL).
// Route: /tx/{id}?token={buyer_token}

import { useEffect, useState, useRef } from "react";

const BETA_SECRET = process.env.REACT_APP_BETA_SECRET || "";

const txId    = window.location.pathname.split("/")[2] || "";
const params  = new URLSearchParams(window.location.search);
const urlToken = params.get("token") || "";
const isSeller = Boolean(BETA_SECRET);

const fmtPrice = (c) => c == null ? "—" : `$${Math.round(c / 100).toLocaleString()}`;
const fmtDate  = (d) => {
  if (!d) return "—";
  const [y, m, day] = d.slice(0, 10).split("-");
  return `${m}/${day}/${y}`;
};
const fmtTs = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const STATUS_ICON = { done: "✓", pending: "○", skipped: "–" };
const STATUS_COLOR = { done: "#7ecfb0", pending: "#5a8a78", skipped: "#3a5a4a" };

const css = `
  *{box-sizing:border-box}
  body{margin:0;font-family:'Inter','Outfit',sans-serif;background:#0a100c;color:#cfe6da}
  .tx-wrap{min-height:100vh;display:flex;flex-direction:column}
  .tx-top{display:flex;align-items:center;gap:12px;padding:12px 20px;border-bottom:1px solid #1c2b25;background:#0e1410;flex-shrink:0;flex-wrap:wrap}
  .tx-brand{font-family:'DM Mono',monospace;font-size:13px;font-weight:700;color:#7ecfb0;letter-spacing:0.2em;cursor:pointer}
  .tx-addr{font-family:'DM Mono',monospace;font-size:11px;color:#5a8a78;letter-spacing:0.06em}
  .tx-role{margin-left:auto;font-family:'DM Mono',monospace;font-size:10px;letter-spacing:0.1em;text-transform:uppercase;background:#1c2b25;color:#7aad98;padding:3px 10px;border-radius:10px}
  .tx-body{flex:1;max-width:900px;width:100%;margin:0 auto;padding:24px 16px 60px;display:grid;grid-template-columns:1fr 340px;gap:24px;align-items:start}
  @media(max-width:700px){.tx-body{grid-template-columns:1fr}}
  .tx-panel{background:#0e1410;border:1px solid #1c2b25;border-radius:16px;padding:20px}
  .tx-panel-h{font-family:'Playfair Display',serif;font-size:17px;font-weight:700;color:#cfe6da;margin:0 0 16px}
  .tx-milestone{display:flex;align-items:flex-start;gap:12px;padding:10px 0;border-bottom:1px solid #111c16;cursor:default}
  .tx-milestone:last-child{border-bottom:none}
  .tx-milestone.seller-click{cursor:pointer}
  .tx-milestone.seller-click:hover .tx-m-icon{opacity:0.7}
  .tx-m-icon{width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;flex-shrink:0;margin-top:1px;transition:opacity 0.15s}
  .tx-m-body{flex:1}
  .tx-m-title{font-size:13px;color:#cfe6da;font-weight:500}
  .tx-m-date{font-size:11px;color:#5a8a78;font-family:'DM Mono',monospace;margin-top:2px}
  .tx-m-done .tx-m-title{color:#7ecfb0}
  .tx-m-skipped .tx-m-title{color:#3a5a4a;text-decoration:line-through}
  .tx-detail-row{display:flex;justify-content:space-between;align-items:baseline;padding:7px 0;border-bottom:1px solid #111c16;font-size:12px}
  .tx-detail-row:last-child{border-bottom:none}
  .tx-detail-label{color:#5a8a78;font-family:'DM Mono',monospace}
  .tx-detail-val{color:#cfe6da;font-weight:500;text-align:right}
  .tx-chip{font-size:10px;font-family:'DM Mono',monospace;padding:2px 8px;border-radius:8px;background:#1c2b25;color:#7aad98;margin-left:4px;letter-spacing:0.05em}
  .tx-notes{margin-top:20px}
  .tx-note{background:#111c16;border-radius:10px;padding:10px 14px;margin-bottom:8px}
  .tx-note-author{font-size:10px;font-family:'DM Mono',monospace;color:#5a8a78;margin-bottom:4px;letter-spacing:0.06em}
  .tx-note-text{font-size:13px;color:#cfe6da;line-height:1.5}
  .tx-note-ts{font-size:10px;color:#3a5a4a;font-family:'DM Mono',monospace;margin-top:4px}
  .tx-note-form{display:flex;flex-direction:column;gap:8px;margin-top:12px}
  .tx-note-input{background:#111c16;border:1px solid #1c2b25;color:#cfe6da;border-radius:8px;padding:8px 12px;font-size:13px;font-family:inherit;resize:vertical;min-height:64px;outline:none}
  .tx-note-input:focus{border-color:#2d7a62}
  .tx-note-input::placeholder{color:#3a5a4a}
  .tx-btn{background:#1c5448;color:#7ecfb0;border:none;border-radius:8px;padding:9px 16px;font-size:12px;font-weight:600;cursor:pointer;font-family:'DM Mono',monospace;letter-spacing:0.06em;transition:background 0.15s}
  .tx-btn:hover{background:#2d7a62}
  .tx-btn:disabled{opacity:0.5;cursor:not-allowed}
  .tx-link-box{background:#111c16;border:1px solid #1c2b25;border-radius:10px;padding:12px 14px;font-size:11px;color:#5a8a78;word-break:break-all;cursor:pointer;transition:background 0.15s;margin-top:12px}
  .tx-link-box:hover{background:#1a2922}
  .tx-link-label{font-size:10px;font-family:'DM Mono',monospace;color:#3a5a4a;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:4px}
  .tx-copy-hint{font-size:10px;font-family:'DM Mono',monospace;color:#7ecfb0;margin-top:4px}
  .tx-empty{text-align:center;padding:60px 20px;color:#5a8a78}
  .tx-err{color:#e67a7a;font-size:13px;text-align:center;padding:40px}
`;

function MilestoneRow({ m, onToggle, role }) {
  const canToggle = role === "seller" && m.id !== "offer_accepted";
  const cls = `tx-milestone${m.status === "done" ? " tx-m-done" : m.status === "skipped" ? " tx-m-skipped" : ""}${canToggle ? " seller-click" : ""}`;
  return (
    <div className={cls} onClick={canToggle ? () => onToggle(m.id, m.status) : undefined} title={canToggle ? "Click to toggle" : undefined}>
      <div className="tx-m-icon" style={{ background: STATUS_COLOR[m.status] + "22", color: STATUS_COLOR[m.status] }}>
        {STATUS_ICON[m.status]}
      </div>
      <div className="tx-m-body">
        <div className="tx-m-title">{m.title}</div>
        <div className="tx-m-date">{fmtDate(m.due)}</div>
      </div>
    </div>
  );
}

export default function TransactionApp() {
  const [tx, setTx] = useState(null);
  const [role, setRole] = useState(null);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);

  const headers = isSeller
    ? { "Content-Type": "application/json", "x-beta-token": BETA_SECRET }
    : { "Content-Type": "application/json" };

  useEffect(() => {
    if (!txId) { setErr("No transaction ID in URL."); return; }
    const url = `/api/transactions?id=${encodeURIComponent(txId)}${urlToken ? `&token=${encodeURIComponent(urlToken)}` : ""}`;
    fetch(url, { headers: isSeller ? { "x-beta-token": BETA_SECRET } : {} })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) { setErr(d.error); return; }
        setTx(d.transaction);
        setRole(d.role);
      })
      .catch((e) => setErr(e.message));
  }, []);

  const toggleMilestone = async (milestoneId, current) => {
    const next = current === "done" ? "pending" : "done";
    const res = await fetch("/api/transactions", {
      method: "POST",
      headers,
      body: JSON.stringify({ action: "update_milestone", id: txId, milestone_id: milestoneId, status: next }),
    });
    const data = await res.json();
    if (res.ok) setTx((t) => ({ ...t, milestones: data.milestones }));
  };

  const submitNote = async () => {
    if (!note.trim()) return;
    setNoteBusy(true);
    try {
      const res = await fetch("/api/transactions", {
        method: "POST",
        headers,
        body: JSON.stringify({
          action: "add_note", id: txId,
          token: urlToken || undefined,
          note: note.trim(),
          author: role === "seller" ? "Seller" : (tx?.buyer_name || "Buyer"),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setTx((t) => ({ ...t, notes: [...(t.notes || []), data.note] }));
        setNote("");
      }
    } finally { setNoteBusy(false); }
  };

  const copyLink = () => {
    if (!tx?.buyer_token) return;
    const link = `${window.location.origin}/tx/${tx.id}?token=${tx.buyer_token}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 2000);
    });
  };

  if (err) return (
    <>
      <style>{css}</style>
      <div className="tx-wrap"><div className="tx-err">{err}</div></div>
    </>
  );

  if (!tx) return (
    <>
      <style>{css}</style>
      <div className="tx-wrap"><div className="tx-empty">Loading…</div></div>
    </>
  );

  const milestones = tx.milestones || [];
  const notes = tx.notes || [];
  const doneCount = milestones.filter((m) => m.status === "done").length;

  return (
    <>
      <style>{css}</style>
      <div className="tx-wrap">
        <header className="tx-top">
          <span className="tx-brand" onClick={() => window.location.href = "/"}>6zero.ai</span>
          <span className="tx-addr">{tx.property_address}</span>
          <span className="tx-role">{role}</span>
        </header>

        <div className="tx-body">
          {/* Left: Timeline */}
          <div>
            <div className="tx-panel">
              <h2 className="tx-panel-h">
                Timeline
                <span style={{ fontSize: 12, fontFamily: "'DM Mono',monospace", fontWeight: 400, color: "#5a8a78", marginLeft: 10 }}>
                  {doneCount}/{milestones.length} complete
                </span>
              </h2>
              {milestones.map((m) => (
                <MilestoneRow key={m.id} m={m} onToggle={toggleMilestone} role={role} />
              ))}
              {role === "seller" && (
                <div style={{ fontSize: 10, color: "#3a5a4a", fontFamily: "'DM Mono',monospace", marginTop: 10, letterSpacing: "0.05em" }}>
                  Click any milestone to mark done / pending
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="tx-panel" style={{ marginTop: 16 }}>
              <h2 className="tx-panel-h">Notes</h2>
              {notes.length === 0 && (
                <div style={{ fontSize: 13, color: "#3a5a4a", marginBottom: 12, fontStyle: "italic" }}>No notes yet.</div>
              )}
              {notes.map((n) => (
                <div key={n.id} className="tx-note">
                  <div className="tx-note-author">{n.author}</div>
                  <div className="tx-note-text">{n.text}</div>
                  <div className="tx-note-ts">{fmtTs(n.created_at)}</div>
                </div>
              ))}
              <div className="tx-note-form">
                <textarea
                  className="tx-note-input"
                  placeholder="Add a note visible to both parties…"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <button className="tx-btn" disabled={noteBusy || !note.trim()} onClick={submitNote}>
                  {noteBusy ? "Posting…" : "Post note"}
                </button>
              </div>
            </div>
          </div>

          {/* Right: Details */}
          <div>
            <div className="tx-panel">
              <h2 className="tx-panel-h">Deal Summary</h2>
              {tx.ai_valuation?.low && (
                <div style={{ background: "#0a1e14", border: "1px solid #1c5448", borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "#5a8a78", letterSpacing: "0.1em", marginBottom: 4 }}>AI FAIR-PRICE ESTIMATE</div>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, color: "#7ecfb0" }}>
                    {fmtPrice(tx.ai_valuation.low)} – {fmtPrice(tx.ai_valuation.high)}
                  </div>
                  <div style={{ fontSize: 11, color: "#5a8a78", marginTop: 3 }}>
                    Accepted at {fmtPrice(tx.accepted_price_cents)}
                    {tx.ai_valuation.midpoint && (
                      <span style={{ marginLeft: 8, color: tx.accepted_price_cents > tx.ai_valuation.high ? "#e6c97a" : tx.accepted_price_cents < tx.ai_valuation.low ? "#7ecfb0" : "#9bbfb2" }}>
                        ({tx.accepted_price_cents > tx.ai_valuation.high ? "above range" : tx.accepted_price_cents < tx.ai_valuation.low ? "below range" : "within range"})
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: "#3a5a4a", marginTop: 4, fontFamily: "'DM Mono',monospace" }}>Same estimate shown to both parties</div>
                </div>
              )}
              <div className="tx-detail-row">
                <span className="tx-detail-label">Accepted price</span>
                <span className="tx-detail-val">{fmtPrice(tx.accepted_price_cents)}</span>
              </div>
              <div className="tx-detail-row">
                <span className="tx-detail-label">Closing date</span>
                <span className="tx-detail-val">{fmtDate(tx.closing_date)}</span>
              </div>
              <div className="tx-detail-row">
                <span className="tx-detail-label">Financing</span>
                <span className="tx-detail-val" style={{ textTransform: "capitalize" }}>{tx.financing_type || "—"}</span>
              </div>
              <div className="tx-detail-row">
                <span className="tx-detail-label">Contingencies</span>
                <span className="tx-detail-val">
                  {tx.contingency_inspection && <span className="tx-chip">inspection</span>}
                  {tx.contingency_financing  && <span className="tx-chip">financing</span>}
                  {tx.contingency_appraisal  && <span className="tx-chip">appraisal</span>}
                  {!tx.contingency_inspection && !tx.contingency_financing && !tx.contingency_appraisal && "none"}
                </span>
              </div>
              {role === "seller" && (
                <>
                  <div className="tx-detail-row">
                    <span className="tx-detail-label">Buyer</span>
                    <span className="tx-detail-val">{tx.buyer_name || "—"}</span>
                  </div>
                  <div className="tx-detail-row">
                    <span className="tx-detail-label">Buyer email</span>
                    <span className="tx-detail-val" style={{ fontSize: 11, wordBreak: "break-all" }}>{tx.buyer_email || "—"}</span>
                  </div>
                </>
              )}
            </div>

            {role === "seller" && tx.buyer_token && (
              <div className="tx-panel" style={{ marginTop: 16 }}>
                <h2 className="tx-panel-h" style={{ fontSize: 13 }}>Share with Buyer</h2>
                <div style={{ fontSize: 12, color: "#5a8a78", marginBottom: 8 }}>
                  Send this link to the buyer so they can follow along.
                </div>
                <div className="tx-link-box" onClick={copyLink}>
                  <div className="tx-link-label">Buyer link</div>
                  <div style={{ color: "#9bbfb2", wordBreak: "break-all" }}>
                    {window.location.origin}/tx/{tx.id}?token=…
                  </div>
                  {copied
                    ? <div className="tx-copy-hint">Copied!</div>
                    : <div className="tx-copy-hint">Click to copy full link</div>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
